import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { authHeader } from "@/lib/token";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Loader2, RefreshCw, ShieldAlert, Upload, Bell, Search, CheckCheck, Fingerprint } from "lucide-react";

const euro = (value) => `${(Number(value) || 0).toFixed(2).replace(".", ",")} €`;
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const TRIAL_MAX_DAYS = 30;

/** Interrupteur « Essai gratuit » + nombre de jours (0 = pas d'essai). */
function TrialSetting({ product, setProduct }) {
  const days = Number(product.trial_days) || 0;
  const on = days > 0;
  const setDays = (value) => setProduct({ ...product, trial_days: Math.min(TRIAL_MAX_DAYS, Math.max(0, parseInt(value, 10) || 0)) });
  return (
    <div data-testid="trial-setting">
      <div className="flex items-center justify-between">
        <Label className="text-white/60 text-xs">Essai gratuit</Label>
        <button type="button" data-testid="trial-toggle" onClick={() => setDays(on ? 0 : 3)} className={`w-10 h-5 rounded-full transition-colors relative ${on ? "bg-emerald-500" : "bg-white/10"}`}>
          <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 left-0 transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
        </button>
      </div>
      {on && (
        <div className="flex items-center gap-2 mt-1">
          <Input data-testid="trial-days" type="number" min="1" max={TRIAL_MAX_DAYS} value={days} onChange={(e) => setDays(Math.max(1, parseInt(e.target.value, 10) || 1))} className="bg-black/30 border-white/10 rounded-lg text-sm w-24" />
          <span className="text-xs text-white/40">jour{days > 1 ? "s" : ""} (1 à {TRIAL_MAX_DAYS})</span>
        </div>
      )}
      <p className="text-[11px] text-white/30 mt-1">
        {!on ? "Pas d'essai : le bouton « Essayer gratuitement » n'apparaît pas sur la page du produit."
          : product.downloadable ? "Une clé de licence d'essai, une fois par client et par serveur (même avec un autre compte)."
          : "Le client donne le nom et le token de son bot, qui tourne jusqu'à la fin de l'essai. Une fois par bot Discord."}
      </p>
    </div>
  );
}

/** Envoi d'une vidéo MP4/WebM depuis l'ordinateur, par morceaux de 2 Mo (barre de progression). */
export async function uploadProductVideo(file, onProgress) {
  const headers = authHeader();
  const start = await axios.post(`${API}/admin/videos`, { filename: file.name, content_type: file.type, size: file.size }, { headers });
  const { id, chunk_size: size, chunks } = start.data;
  for (let n = 0; n < chunks; n += 1) {
    const part = file.slice(n * size, Math.min(file.size, (n + 1) * size));
    await axios.put(`${API}/admin/videos/${id}/chunks/${n}`, part, { headers: { ...headers, "Content-Type": "application/octet-stream" } });
    onProgress && onProgress(Math.round(((n + 1) / chunks) * 100));
  }
  const done = await axios.post(`${API}/admin/videos/${id}/complete`, {}, { headers });
  return done.data.url;
}

function VideoUploadButton({ onUploaded }) {
  const [progress, setProgress] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["video/mp4", "video/webm"].includes(file.type)) { toast.error("Vidéo MP4 ou WebM uniquement"); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error("Vidéo trop lourde (100 Mo max) : compresse-la ou mets un lien YouTube"); return; }
    setProgress(0);
    try {
      const url = await uploadProductVideo(file, setProgress);
      onUploaded(url);
      toast.success("Vidéo envoyée : enregistre le produit pour l'afficher");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi de la vidéo impossible");
    }
    setProgress(null);
  };
  return (
    <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-xs text-white/70 hover:text-white hover:border-blue-500/50 cursor-pointer shrink-0 ${progress !== null ? "pointer-events-none opacity-70" : ""}`} data-testid="video-upload">
      {progress !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
      {progress !== null ? `Envoi… ${progress} %` : "Envoyer une vidéo"}
      <input type="file" accept="video/mp4,video/webm" className="hidden" onChange={pick} data-testid="video-upload-input" />
    </label>
  );
}

/** Réglages en plus d'un produit : vidéo, essai gratuit, contenu du pack, rôle Discord. */
export function ProductExtrasFields({ product, setProduct, shopBots }) {
  const includes = product.includes || [];
  const others = shopBots.filter((b) => b.id !== product.id);
  const toggleInclude = (id) => setProduct({
    ...product,
    includes: includes.includes(id) ? includes.filter((i) => i !== id) : [...includes, id],
  });
  return (
    <div className="border-t border-white/5 pt-3 space-y-3" data-testid="product-extras-fields">
      <div>
        <Label className="text-white/60 text-xs">Vidéo de présentation (fichier depuis ton ordinateur ou lien YouTube)</Label>
        <div className="flex gap-2 mt-1 items-center">
          <VideoUploadButton onUploaded={(url) => setProduct({ ...product, video_url: url })} />
          <Input data-testid="video-url-input" value={product.video_url || ""} onChange={(e) => setProduct({ ...product, video_url: e.target.value })} placeholder="ou https://www.youtube.com/watch?v=..." className="bg-black/30 border-white/10 rounded-lg text-sm" />
        </div>
        {product.video_url && !/youtu/.test(product.video_url) && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={product.video_url.startsWith("/api/") ? `${API}${product.video_url.slice(4)}` : product.video_url} controls preload="metadata" className="mt-2 w-full max-w-sm rounded-lg border border-white/10 bg-black" data-testid="video-preview" />
        )}
        <p className="text-[11px] text-white/30 mt-1">MP4 ou WebM, 100 Mo max. Vide : la vidéo de présentation fournie avec le site est affichée (si ce produit en a une).</p>
      </div>
      <TrialSetting product={product} setProduct={setProduct} />
      {others.length > 0 && (
        <div>
          <Label className="text-white/60 text-xs">C'est un pack ? Produits inclus</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {others.map((b) => (
              <button key={b.id} type="button" onClick={() => toggleInclude(b.id)} className={`px-2.5 py-1 rounded text-xs border transition-colors ${includes.includes(b.id) ? "bg-blue-500/20 border-blue-500/50 text-blue-300" : "bg-white/5 border-white/10 text-white/40"}`}>
                {b.name}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-white/30 mt-1">Les clients qui ont déjà un de ces produits à vie peuvent passer au pack en payant la différence.</p>
        </div>
      )}
      <div>
        <Label className="text-white/60 text-xs">Lien Tebex (produit FiveM vendu sur Tebex)</Label>
        <Input data-testid="tebex-url-input" value={product.tebex_url || ""} onChange={(e) => setProduct({ ...product, tebex_url: e.target.value.trim() })} placeholder="https://ta-boutique.tebex.io/package/123456" className="bg-black/30 border-white/10 rounded-lg mt-1 text-sm font-mono" />
        <p className="text-[11px] text-white/30 mt-1">Rempli : le bouton « Acheter » mène à Tebex (paiement et attribution au compte Cfx.re de l'acheteur) et le paiement sur le site est désactivé pour ce produit. Vide : vente sur le site.</p>
      </div>
      <div>
        <Label className="text-white/60 text-xs">Rôle Discord donné aux acheteurs (ID du rôle, facultatif)</Label>
        <Input value={product.discord_role_id || ""} onChange={(e) => setProduct({ ...product, discord_role_id: e.target.value.replace(/\D/g, "") })} placeholder="123456789012345678" className="bg-black/30 border-white/10 rounded-lg mt-1 text-sm font-mono" />
      </div>
    </div>
  );
}

const nextVersion = (current) => {
  const parts = String(current || "").split(".").map((p) => parseInt(p, 10));
  if (!parts.length || parts.some(Number.isNaN)) return "";
  parts[parts.length - 1] += 1;
  return parts.join(".");
};

/** Publier une nouvelle version d'un produit téléchargeable. */
export function ReleaseDialog({ product, onClose, onDone }) {
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");
  const [notify, setNotify] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!product) return;
    setVersion(nextVersion(product.current_version));
    setNotes(""); setFile(null); setLink(""); setNotify(true);
  }, [product]);

  const submit = async () => {
    if (!version.trim()) { toast.error("Numéro de version requis"); return; }
    if (file && file.size > 20 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 20 Mo) — utilisez un lien"); return; }
    setSending(true);
    try {
      const payload = { version: version.trim(), notes, notify };
      if (file) {
        payload.file_data = await readAsDataUrl(file);
        payload.filename = file.name;
        payload.content_type = file.type || "application/zip";
      } else if (link.trim()) {
        payload.download_url = link.trim();
      }
      const r = await axios.post(`${API}/admin/shop/bots/${product.id}/release`, payload, { headers: authHeader() });
      toast.success(`Version ${r.data.version} publiée : ${r.data.buyers_updated} client(s) mis à jour, ${r.data.notified} prévenu(s)`);
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la publication");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-[#12121a] border-white/10 sm:max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="w-4 h-4 text-blue-400" />Nouvelle version — {product?.name}</DialogTitle></DialogHeader>
        {product && (
          <div className="space-y-3" data-testid="release-dialog">
            <p className="text-xs text-white/40">Version actuelle : {product.current_version || "—"}. Le nouveau fichier remplace l'ancien pour tous les clients ; leur clé de licence ne change pas.</p>
            <div><Label className="text-white/60 text-xs">Numéro de version</Label><Input data-testid="release-version" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0" className="bg-black/30 border-white/10 rounded-lg mt-1" /></div>
            <div><Label className="text-white/60 text-xs">Nouveautés (affichées sur la page du produit)</Label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder={"- Nouvelle app Banque\n- Correction des appels"} className="w-full bg-black/30 border border-white/10 rounded-lg mt-1 p-3 text-sm text-white resize-none focus:outline-none focus:border-blue-500" /></div>
            <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-2 cursor-pointer hover:border-blue-500/50">
              <input type="file" className="hidden" onChange={(e) => { setFile(e.target.files[0] || null); setLink(""); }} />
              <Upload className="w-4 h-4 text-white/30 shrink-0" />
              <span className="flex-1 text-xs text-white/40 truncate">{file ? file.name : "Nouveau fichier .zip (max 20 Mo)"}</span>
            </label>
            {!file && <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="…ou un lien de téléchargement https://" className="bg-black/30 border-white/10 rounded-lg text-sm" />}
            <p className="text-[11px] text-white/30">Sans fichier ni lien, seule la note de version est ajoutée.</p>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="accent-blue-500" />
              Prévenir les clients (notification + e-mail)
            </label>
            <Button data-testid="release-submit" onClick={submit} disabled={sending} className="w-full bg-blue-600 hover:bg-blue-700 rounded-lg">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publier la version"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Statistiques par produit. */
export function ProductStatsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/stats/products`, { headers: authHeader() })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>;
  if (!data) return <p className="text-white/40 text-sm">Statistiques indisponibles.</p>;
  const t = data.totals;
  return (
    <div data-testid="product-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Chiffre d'affaires", value: euro(t.revenue), color: "text-green-400" },
          { label: "30 derniers jours", value: euro(t.revenue_30d), color: "text-blue-400" },
          { label: "Clients actifs", value: t.active_customers, color: "text-yellow-400" },
          { label: "Essais → achats", value: `${t.trials_converted} / ${t.trials}`, color: "text-purple-400" },
        ].map((card) => (
          <div key={card.label} className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-4">
            <p className="text-xs text-white/40">{card.label}</p>
            <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-end mb-2"><Button size="sm" variant="ghost" onClick={load} className="text-white/40 hover:text-white text-xs"><RefreshCw className="w-3 h-3 mr-1" />Actualiser</Button></div>
      <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-white/40 border-b border-white/5">
              <th className="p-3">Produit</th><th className="p-3 text-right">Ventes</th><th className="p-3 text-right">CA</th><th className="p-3 text-right">30 j</th>
              <th className="p-3 text-right">Actifs</th><th className="p-3 text-right">Expirés</th><th className="p-3 text-right">Essais</th>
              <th className="p-3 text-right">Conversion</th><th className="p-3 text-right">Codes promo</th><th className="p-3 text-right">Passages au pack</th>
            </tr>
          </thead>
          <tbody>
            {data.products.map((p) => (
              <tr key={p.product_id} className="border-b border-white/5 last:border-0">
                <td className="p-3"><span className="font-medium">{p.name}</span>{!p.active_in_shop && <span className="ml-2 text-[10px] text-white/30">(hors boutique)</span>}</td>
                <td className="p-3 text-right">{p.sales}{p.renewals > 0 && <span className="text-white/30 text-xs"> +{p.renewals} renouv.</span>}</td>
                <td className="p-3 text-right text-green-400">{euro(p.revenue)}</td>
                <td className="p-3 text-right text-white/60">{euro(p.revenue_30d)}</td>
                <td className="p-3 text-right">{p.active_customers}{p.subscriptions > 0 && <span className="text-white/30 text-xs"> ({p.subscriptions} auto)</span>}</td>
                <td className="p-3 text-right text-white/50">{p.expired}</td>
                <td className="p-3 text-right">{p.trials}</td>
                <td className="p-3 text-right">{p.trial_conversion_pct === null ? "—" : `${p.trial_conversion_pct} %`}</td>
                <td className="p-3 text-right">{p.promo_uses}</td>
                <td className="p-3 text-right">{p.is_bundle ? p.upgrades : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Licences : dernier contact des serveurs et clés probablement partagées. */
export function LicensesPanel() {
  const [rows, setRows] = useState([]);
  const [suspectOnly, setSuspectOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/licenses`, { headers: authHeader(), params: suspectOnly ? { suspect: true } : {} })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [suspectOnly]);
  useEffect(() => { load(); }, [load]);

  const q = search.toLowerCase();
  const visible = rows.filter((r) => !q || [r.user_email, r.username, r.product_name, r.last_ip, r.key_hint].some((v) => (v || "").toLowerCase().includes(q)));
  const suspects = rows.filter((r) => r.suspect).length;
  return (
    <div data-testid="licenses-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (e-mail, produit, IP...)" className="bg-black/30 border-white/10 rounded-lg text-sm max-w-xs" />
        <button onClick={() => setSuspectOnly(!suspectOnly)} className={`px-3 py-1.5 rounded text-xs border ${suspectOnly ? "bg-red-500/20 border-red-500/40 text-red-300" : "bg-white/5 border-white/10 text-white/50"}`}>
          <ShieldAlert className="w-3 h-3 inline mr-1" />Suspectes seulement{!suspectOnly && suspects > 0 ? ` (${suspects})` : ""}
        </button>
        <Button size="sm" variant="ghost" onClick={load} className="text-white/40 hover:text-white text-xs"><RefreshCw className="w-3 h-3 mr-1" />Actualiser</Button>
      </div>
      <p className="text-xs text-white/30 mb-4">Une clé est « suspecte » quand d'autres serveurs ont essayé de l'utiliser (3 refus ou plus) ou qu'elle a été vue depuis plus de 3 adresses IP. Pour la bloquer : Abonnements &gt; clé &gt; Régénérer ou Désactiver.</p>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-12 text-center"><KeyRound className="w-10 h-10 text-white/10 mx-auto mb-3" /><p className="text-white/30">Aucune licence</p></div>
      ) : (
        <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-white/40 border-b border-white/5">
                <th className="p-3">Client</th><th className="p-3">Produit</th><th className="p-3">Clé</th><th className="p-3">Serveur lié</th>
                <th className="p-3">Dernier contact</th><th className="p-3">IP</th><th className="p-3 text-right">Refus</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.bot_id} className={`border-b border-white/5 last:border-0 ${r.suspect ? "bg-red-500/5" : ""}`}>
                  <td className="p-3"><p>{r.username || "—"}</p><p className="text-xs text-white/30">{r.user_email}</p></td>
                  <td className="p-3"><p>{r.product_name}</p><p className="text-xs text-white/30">{r.is_trial ? "Essai" : r.plan_id}{!r.license_active && " · désactivée"}</p></td>
                  <td className="p-3 font-mono text-xs">{r.key_hint}</td>
                  <td className="p-3 font-mono text-xs">{r.bound ? r.device_hint : <span className="text-white/30">pas encore</span>}</td>
                  <td className="p-3 text-xs">{fmtDateTime(r.last_seen_at)}</td>
                  <td className="p-3 text-xs"><span className="font-mono">{r.last_ip || "—"}</span>{r.ip_count > 1 && <span className="text-white/30"> ({r.ip_count} IP)</span>}</td>
                  <td className="p-3 text-right">
                    <span className={r.refusals > 0 ? "text-red-400 font-semibold" : "text-white/30"}>{r.refusals}</span>
                    {r.last_refusal && <p className="text-[10px] text-white/30">{fmtDateTime(r.last_refusal.at)} · {r.last_refusal.ip}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TRIAL_STATES = {
  active: { label: "En cours", cls: "bg-emerald-500/15 text-emerald-300" },
  ended: { label: "Terminé", cls: "bg-white/10 text-white/50" },
  converted: { label: "Acheté", cls: "bg-blue-500/15 text-blue-300" },
  deleted: { label: "Supprimé", cls: "bg-white/10 text-white/40" },
};

/** Essais gratuits : activer / retirer l'essai de chaque produit et suivre les essais en cours. */
export function TrialsPanel() {
  const [products, setProducts] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [trials, setTrials] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loadingTrials, setLoadingTrials] = useState(true);
  const [ending, setEnding] = useState(null);

  const loadProducts = useCallback(() => {
    axios.get(`${API}/admin/shop/bots`, { headers: authHeader() })
      .then((r) => {
        const list = r.data || [];
        setProducts(list);
        setDrafts(Object.fromEntries(list.map((p) => [p.id, Number(p.trial_days) || 0])));
      })
      .catch(() => setProducts([]));
  }, []);
  const loadTrials = useCallback(() => {
    setLoadingTrials(true);
    axios.get(`${API}/admin/trials`, { headers: authHeader(), params: { state: showAll ? "all" : "active" } })
      .then((r) => setTrials(r.data || []))
      .catch(() => setTrials([]))
      .finally(() => setLoadingTrials(false));
  }, [showAll]);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadTrials(); }, [loadTrials]);

  const save = async (product, days) => {
    setSaving(product.id);
    try {
      await axios.put(`${API}/admin/shop/bots/${product.id}`, { trial_days: days }, { headers: authHeader() });
      setProducts(products.map((p) => (p.id === product.id ? { ...p, trial_days: days } : p)));
      setDrafts({ ...drafts, [product.id]: days });
      toast.success(days > 0 ? `Essai de ${days} jour${days > 1 ? "s" : ""} activé pour ${product.name}` : `Essai retiré de ${product.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(null);
    }
  };
  const endTrial = async (row) => {
    if (!window.confirm(`Terminer maintenant l'essai de ${row.user_email || row.username} (${row.product_name}) ?`)) return;
    setEnding(row.bot_id);
    try {
      await axios.post(`${API}/admin/trials/${row.bot_id}/end`, {}, { headers: authHeader() });
      toast.success("Essai terminé");
      loadTrials();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setEnding(null);
    }
  };

  if (!products) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>;
  return (
    <div className="space-y-8" data-testid="trials-panel">
      <div>
        <h2 className="text-lg font-semibold mb-1">Essai par produit</h2>
        <p className="text-xs text-white/30 mb-3">Active l'essai et choisis sa durée (1 à {TRIAL_MAX_DAYS} jours). Le bouton « Essayer gratuitement » apparaît alors sur la page du produit. Désactive pour le retirer : les essais déjà commencés vont jusqu'à leur fin (ou termine-les plus bas).</p>
        <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl divide-y divide-white/5">
          {products.map((p) => {
            const saved = Number(p.trial_days) || 0;
            const draft = drafts[p.id] ?? saved;
            const on = draft > 0;
            const dirty = draft !== saved;
            return (
              <div key={p.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`trial-row-${p.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm">{p.name}</p>
                  <p className="text-[11px] text-white/30">{p.downloadable ? "Script (clé de licence d'essai)" : "Bot Discord"}{p.active === false ? " · masqué de la boutique" : ""}</p>
                </div>
                <button type="button" data-testid={`trial-toggle-${p.id}`} onClick={() => setDrafts({ ...drafts, [p.id]: on ? 0 : (saved || 3) })} className={`w-10 h-5 rounded-full transition-colors relative ${on ? "bg-emerald-500" : "bg-white/10"}`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 left-0 transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
                <div className="flex items-center gap-2 w-36">
                  {on ? (
                    <>
                      <Input data-testid={`trial-days-${p.id}`} type="number" min="1" max={TRIAL_MAX_DAYS} value={draft} onChange={(e) => setDrafts({ ...drafts, [p.id]: Math.min(TRIAL_MAX_DAYS, Math.max(1, parseInt(e.target.value, 10) || 1)) })} className="bg-black/30 border-white/10 rounded-lg text-sm w-20 h-8" />
                      <span className="text-xs text-white/40">jours</span>
                    </>
                  ) : <span className="text-xs text-white/30">Pas d'essai</span>}
                </div>
                <Button size="sm" data-testid={`trial-save-${p.id}`} onClick={() => save(p, draft)} disabled={!dirty || saving === p.id} className="bg-blue-600 hover:bg-blue-700 rounded-lg h-8 disabled:opacity-30">
                  {saving === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Enregistrer"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h2 className="text-lg font-semibold">{showAll ? "Tous les essais" : "Essais en cours"}</h2>
          <button onClick={() => setShowAll(!showAll)} className="px-3 py-1.5 rounded text-xs border bg-white/5 border-white/10 text-white/50">{showAll ? "Voir seulement ceux en cours" : "Voir tout l'historique"}</button>
          <Button size="sm" variant="ghost" onClick={loadTrials} className="text-white/40 hover:text-white text-xs"><RefreshCw className="w-3 h-3 mr-1" />Actualiser</Button>
        </div>
        {loadingTrials ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
        ) : trials.length === 0 ? (
          <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-8 text-center text-white/30">{showAll ? "Aucun essai pour l'instant" : "Aucun essai en cours"}</div>
        ) : (
          <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-white/40 border-b border-white/5">
                  <th className="p-3">Client</th><th className="p-3">Produit</th><th className="p-3">Début</th><th className="p-3">Fin</th><th className="p-3">État</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {trials.map((t) => (
                  <tr key={t.bot_id} className="border-b border-white/5 last:border-0" data-testid={`trial-${t.bot_id}`}>
                    <td className="p-3"><p>{t.username || "—"}</p><p className="text-xs text-white/30">{t.user_email}</p></td>
                    <td className="p-3">{t.product_name}</td>
                    <td className="p-3 text-xs">{fmtDateTime(t.created_at)}</td>
                    <td className="p-3 text-xs">{fmtDateTime(t.expires_at)}</td>
                    <td className="p-3"><span className={`text-[11px] px-2 py-0.5 rounded ${(TRIAL_STATES[t.state] || TRIAL_STATES.ended).cls}`}>{(TRIAL_STATES[t.state] || TRIAL_STATES.ended).label}</span></td>
                    <td className="p-3 text-right">
                      {t.state === "active" && (
                        <button data-testid={`end-trial-${t.bot_id}`} onClick={() => endTrial(t)} disabled={ending === t.bot_id} className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50">
                          {ending === t.bot_id ? "..." : "Terminer"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const ALERT_LABELS = {
  shared_key: "Clé partagée (2e serveur)",
  disabled_key_in_use: "Clé désactivée toujours utilisée",
};

/** Protection : alertes d'utilisation frauduleuse, traçage d'une fuite, webhook. */
export function ProtectionPanel() {
  const [alerts, setAlerts] = useState([]);
  const [resolved, setResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [webhook, setWebhook] = useState("");
  const [envWebhook, setEnvWebhook] = useState(false);
  const [savingHook, setSavingHook] = useState(false);
  const [traceText, setTraceText] = useState("");
  const [matches, setMatches] = useState(null);
  const [tracing, setTracing] = useState(false);

  const loadAlerts = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/license-alerts`, { headers: authHeader(), params: { resolved } })
      .then((r) => setAlerts(r.data || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  }, [resolved]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);
  useEffect(() => {
    axios.get(`${API}/admin/alert-webhook`, { headers: authHeader() })
      .then((r) => { setWebhook(r.data?.webhook || ""); setEnvWebhook(!!r.data?.env_configured); })
      .catch(() => {});
  }, []);

  const resolveAlert = async (id) => {
    try {
      await axios.post(`${API}/admin/license-alerts/${id}/resolve`, {}, { headers: authHeader() });
      setAlerts(alerts.filter((a) => a.id !== id));
    } catch { toast.error("Erreur"); }
  };
  const saveWebhook = async () => {
    setSavingHook(true);
    try {
      await axios.put(`${API}/admin/alert-webhook`, { webhook }, { headers: authHeader() });
      toast.success("Webhook enregistré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSavingHook(false); }
  };
  const runTrace = async () => {
    setTracing(true);
    try {
      const r = await axios.post(`${API}/admin/watermark/trace`, { text: traceText }, { headers: authHeader() });
      setMatches(r.data?.matches || []);
    } catch { toast.error("Erreur"); setMatches([]); }
    finally { setTracing(false); }
  };

  return (
    <div className="space-y-8" data-testid="protection-panel">
      {/* Alertes */}
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Bell className="w-4 h-4 text-red-400" />Alertes de triche</h2>
          <button onClick={() => setResolved(!resolved)} className={`px-3 py-1.5 rounded text-xs border ${resolved ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-white/50"}`}>{resolved ? "Voir les non traitées" : "Voir les traitées"}</button>
          <Button size="sm" variant="ghost" onClick={loadAlerts} className="text-white/40 hover:text-white text-xs"><RefreshCw className="w-3 h-3 mr-1" />Actualiser</Button>
        </div>
        <p className="text-xs text-white/30 mb-3">Une alerte apparaît quand une clé est utilisée sur un 2e serveur (clé partagée / revendue) ou quand une clé désactivée continue d'être utilisée. Pour bloquer un tricheur : Abonnements &gt; sa clé &gt; Désactiver ou Régénérer.</p>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
        ) : alerts.length === 0 ? (
          <div className="bg-[#0f1019] border border-white/[0.07] rounded-2xl p-8 text-center text-white/30">{resolved ? "Aucune alerte traitée" : "Aucune alerte : tout est normal"}</div>
        ) : (
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className="bg-[#12121a] border border-red-500/20 rounded-lg p-3 flex items-start gap-3">
                <ShieldAlert className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{ALERT_LABELS[a.kind] || a.kind}{a.count > 1 && <span className="text-white/40"> · {a.count}×</span>}</p>
                  <p className="text-xs text-white/40">{a.message}</p>
                  <p className="text-[11px] text-white/30 mt-1">{a.user_email || "—"} · {a.product_id} · IP {a.ip || "—"}{a.server_name ? ` · « ${a.server_name} »` : ""} · {fmtDateTime(a.at)}</p>
                </div>
                {!resolved && <button onClick={() => resolveAlert(a.id)} className="text-xs text-white/40 hover:text-white flex items-center gap-1 shrink-0"><CheckCheck className="w-3.5 h-3.5" />Traiter</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Traçage d'une fuite */}
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-2"><Fingerprint className="w-4 h-4 text-blue-400" />Retrouver l'origine d'une fuite</h2>
        <p className="text-xs text-white/30 mb-2">Chaque téléchargement porte une empreinte invisible propre à l'acheteur. Colle ici le contenu d'un fichier fuité (ou juste la ligne « -- build ... » / « vpwm:... ») : on retrouve le client.</p>
        <textarea value={traceText} onChange={(e) => setTraceText(e.target.value)} rows={4} placeholder="Colle le contenu du fichier suspect ici..." className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-white font-mono focus:outline-none focus:border-blue-500" />
        <Button size="sm" onClick={runTrace} disabled={tracing || !traceText.trim()} className="bg-blue-600 hover:bg-blue-700 rounded-lg mt-2"><Search className="w-3.5 h-3.5 mr-1" />{tracing ? "Recherche..." : "Identifier"}</Button>
        {matches !== null && (
          matches.length === 0 ? (
            <p className="text-sm text-white/40 mt-3">Aucune empreinte reconnue dans ce texte.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {matches.map((m) => (
                <div key={m.bot_id} className="bg-[#0f1019] border border-white/[0.09] rounded-2xl p-3 text-sm">
                  <p className="text-red-300 font-medium">Fuite provenant de : {m.user_email || m.username || "—"}</p>
                  <p className="text-xs text-white/40">{m.product_name} · clé <span className="font-mono">{m.license_key}</span> · {m.license_active ? "clé active" : "clé désactivée"}</p>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Webhook */}
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-2"><Bell className="w-4 h-4 text-white/60" />Webhook d'alerte (Discord)</h2>
        <p className="text-xs text-white/30 mb-2">Reçois les alertes directement sur un salon Discord. Colle l'URL du webhook (Paramètres du salon &gt; Intégrations &gt; Webhooks).{envWebhook && " Un webhook est déjà configuré côté serveur (variable d'environnement)."}</p>
        <div className="flex flex-wrap items-center gap-2 max-w-2xl">
          <Input value={webhook} onChange={(e) => setWebhook(e.target.value)} placeholder="https://discord.com/api/webhooks/..." className="bg-black/30 border-white/10 rounded-lg text-sm flex-1 min-w-[240px]" />
          <Button size="sm" onClick={saveWebhook} disabled={savingHook} className="bg-blue-600 hover:bg-blue-700 rounded-lg">{savingHook ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button>
        </div>
      </div>
    </div>
  );
}
